import * as React from 'react'
import * as Redux from 'react-redux'
import styled, { css } from 'styled-components'

import * as header from '@ulule/header'
import { useDramaticallyInefficientMediaQuery } from '@owl-nest/hooks'
import { ConfiguredNavMenu, Wrapper, Screen, PM_MENU_WIDTH } from '@owl-nest/project-manager-nav'
import { message } from '@owl-nest/redux-wrapper/components'
import * as plume from '@ulule/owl-kit-components/next'
import * as models from '@owl-nest/models'
import * as env from '@owl-nest/config'
import * as api from '@owl-nest/api-client/latest'
import { useAuth } from '@owl-nest/redux-wrapper/hooks'

import { useDisplayMode, DisplayMode as DisplayModeType } from '../hooks/useDisplayMode'
import * as projectModel from '../models/project'
import { RootState } from '../../reducers'
import * as UFE from '../../UFE'

import { PreviewBar } from '../views/detail/components/PreviewBar'

import { EDIT_MODE_TABLET_BREAKPOINT } from '../views/detail/constants'
import { LilSam } from '../components/LilSam'

type HeaderLayoutProps = {
  headerRef?: React.Ref<HTMLElement>
  className?: string
  isHeaderSticky?: boolean
}

export function HeaderLayoutComponent({
  headerRef,
  className,
  isHeaderSticky,
}: HeaderLayoutProps): React.ReactElement<HeaderLayoutProps> {
  const project = Redux.useSelector<RootState, projectModel.Project>((state) => state.application.project.data)

  const role = models.project.getEditingUserRole(project, UFE.CURRENT_USER.is_staff)
  const userConfig = env.useUserEnv()

  const stickyRefHeader = React.useRef<HTMLDivElement>(null)

  const displayMode = useDisplayMode(project)
  const isEditMode = displayMode === 'edit'

  const breakpoint = isEditMode ? EDIT_MODE_TABLET_BREAKPOINT : plume.BREAKPOINTS.TABLET

  const isTablet = useDramaticallyInefficientMediaQuery(breakpoint)

  const auth = useAuth()
  const isOwner = auth.type === 'loggedin' && auth.user.id === project.owner.id

  // If owner menu is visible or the custom color is white, set color to blue.
  // Otherwise, use color chosen by user.
  const backgroundColor = project.background_color?.[userConfig.lang]
  const theme = {
    colors: {
      primary: isEditMode
        ? plume.COLORS.PRIMARY_BLUE_500
        : backgroundColor && plume.COLORS.hexToRgb(backgroundColor, 0) !== 'rgb(255,255,255)'
          ? backgroundColor
          : plume.COLORS.PRIMARY_BLUE_500,
    },
  }

  function setRef(node: HTMLDivElement): void {
    ;(stickyRefHeader as any).current = node
    if (headerRef) {
      if (typeof headerRef === 'function') {
        headerRef(node)
      } else {
        ;(headerRef as any).current = node
      }
    }
  }

  return (
    <HeaderWrapper ref={setRef} $displayMode={displayMode} className={className}>
      {displayMode === 'preview' && <PreviewBar />}
      <header.Header theme={theme} enableSubnav={false} />

      {isEditMode && !isTablet && (
        <ConfiguredNavMenu
          mobileBreakpoint={isEditMode ? EDIT_MODE_TABLET_BREAKPOINT : undefined}
          project={project}
          role={role}
          screen={Screen.MY_PROJECT}
        />
      )}

      {displayMode !== 'preview' && (
        <BannerContainer $isEditMode={isEditMode} $breakpoint={breakpoint}>
          <message.Banner
            key="error-alert"
            contexts={[
              'bo.success',
              'extratime',
              'extratime_billing',
              'extratime_ended',
              'funded',
              'funded_billing',
              'mobile',
              'pending-changes',
            ]}
          />
        </BannerContainer>
      )}

      <HiddenOnSticky $isHidden={isHeaderSticky}>
        {isOwner && project.status !== api.ProjectStatus.REFUSED && <LilSam />}
      </HiddenOnSticky>
    </HeaderWrapper>
  )
}
export const HeaderLayout = styled(HeaderLayoutComponent)``

const BannerContainer = styled.div<{ $isEditMode: boolean; $breakpoint: string }>`
  position: absolute;
  z-index: 0;

  @media screen and ${({ $breakpoint }) => $breakpoint} {
    margin-left: ${({ $isEditMode }) => ($isEditMode ? PM_MENU_WIDTH : '0')};
    width: ${({ $isEditMode }) => ($isEditMode ? `calc(100% - ${PM_MENU_WIDTH})` : '100%')};
  }
`

const HiddenOnSticky = styled.div<{ $isHidden?: boolean }>`
  opacity: 1;
  position: absolute;
  top: 0px;
  transition: 0.1s;
  width: 100%;
  z-index: ${plume.ZINDEX.NAVIGATION_MENU};
  display: none;

  @media screen and ${EDIT_MODE_TABLET_BREAKPOINT} {
    display: block;
  }

  ${({ $isHidden }) => {
    if ($isHidden) {
      return css`
        opacity: 0;
        top: -5px;
      `
    } else {
      return css`
        opacity: 1;
      `
    }
  }};
`

const HeaderWrapper = styled.div<{ $displayMode: DisplayModeType }>`
  pointer-events: none;
  z-index: ${plume.ZINDEX.HEADER};

  ${header.styles.HeaderContent}, ${BannerContainer}, ${Wrapper}, ${HiddenOnSticky} {
    pointer-events: all;
  }
`
